import { motion } from 'motion/react';
import { Link, Navigate, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileCheck,
  Network,
  ShieldCheck,
} from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import softwareOneLogo from '../components/images/softwareonelogo.webp';
import jioLogo from '../components/images/jioLogo-removebg-preview.png';
import redingtonLogo from '../components/images/RedLogo.png';
import tataTeleLogo from '../components/images/tatatelelogo.png';

const caseStudies = [
  {
    id: 'softwareone',
    client: 'SoftwareOne',
    service: 'Private-label cloud commerce',
    title: 'Global cloud commerce transformation',
    industry: 'Global Cloud Distribution',
    status: 'Live platform',
    logo: softwareOneLogo,
    summary:
      'A private-label cloud commerce platform for a global Microsoft cloud distributor that needed one governed operating layer across geographies, currencies, partner tiers, taxation, compliance, customer self-care, and cloud resource management.',
    metrics: [
      { value: '35+', label: 'geographies live' },
      { value: '10+', label: 'currencies' },
      { value: '10K+', label: 'users enabled' },
      { value: '6+ yrs', label: 'zero downtime' },
    ],
    challenge: {
      title: 'Scaling cloud distribution without fragmenting operations',
      points: [
        'Country-specific commerce, customer experience, and compliance controls across a large global footprint.',
        'Multi-currency pricing, local taxation, revenue recognition, partner rewards, and channel program complexity.',
        'Need for one operating window across marketplace operations, integrations, support, and financial governance.',
      ],
    },
    solution: {
      title: 'Private-label marketplace and governed commerce layer',
      points: [
        'AppGallop delivered a private-labeled cloud commerce platform supporting Microsoft cloud offerings, customer self-care, partner storefronts, and extensibility for additional providers.',
        'Multi-tenant and multi-currency operations brought price book, taxation, partner lifecycle, revenue allocation, reseller rewards, and country-level governance into a single commercial model.',
        'Integrated support operations connected internal systems, ServiceNow, Jira, governed APIs, CMP-enabled resource management, dashboards, and operational workflows across regions.',
      ],
    },
    outcomes: [
      'Tax compliance configured across operating markets',
      'Millions of dollars in transactions processed through the platform',
      'Channel program, T2 storefront, and customer self-care enablement',
      'GDPR compliant and Microsoft NCE compliant ahead of schedule',
      'CMP-enabled resource management and private-label API governance',
      'Six-plus years of stable platform operations with zero downtime reported for the commerce layer',
    ],
    quote:
      'They are a smart, ambitious, customer-focused group. Most important to us is that they take feedback and execute on it with speed and quality.',
    quoteBy: 'CTO and Board Member',
    tags: ['Cloud commerce', 'Private label', 'Partner channels'],
  },
  {
    id: 'jio-platforms',
    client: 'Jio Platforms Limited',
    service: 'Multi-service integration',
    title: 'OMNI services integration with telco legacy systems',
    industry: 'Telecom and Digital Services',
    status: 'Enterprise telco case',
    logo: jioLogo,
    summary:
      'A telco-grade OMNI services platform that connected new digital service journeys with legacy OSS/BSS, CRM, ERP, billing, taxation, accounting, service assurance, support, logistics, and rule-management systems.',
    metrics: [
      { value: 'OSS/BSS', label: 'legacy integration' },
      { value: '750+', label: 'support users' },
      { value: '4-level', label: 'ticket taxonomy' },
      { value: 'AR/AP', label: 'accounting entries' },
    ],
    challenge: {
      title: 'Introducing a new platform into a legacy telco ecosystem',
      points: [
        'The platform had to integrate with core OSS/BSS, service desk, CRM, ERP, third-party applications, and telco operational systems.',
        'Compliance requirements covered internal processes, external rules, accounting entries, tax, credit policy, service rules, SLA, and customer data handling.',
        'The business needed a single window for supplier pricing, market pricing, service sales, invoicing, billing, collections, and support operations.',
      ],
    },
    solution: {
      title: 'SOA-based enterprise platform with API-led service integration',
      points: [
        'AppGallop connected cloud, services, and subscription offering journeys with legacy telco systems through an SOA-based integration layer that reduced load on parent platforms.',
        'The platform generated accounting entries for AR, AP, TDS, payments, reversals, settlements, partial refunds, credit notes, debit notes, customer segmentation, and credit-policy controls.',
        'Support operations were integrated with NSD, SLA processes, customer-segment queues, ticket routing rules, auto/manual assignment, escalation paths, and service assurance workflows.',
      ],
    },
    outcomes: [
      'Automated customer onboarding and verification',
      'GST, TDS, reversals, credit notes, and credit-policy compliance',
      'Payment received and reconciliation workflows',
      'Invoicing, billing, rating, charging, dunning, and split-invoice rules',
      'Integrations across Enable Service, MACD, VAS, roster management, service assurance, and logistics',
      'Unified operating visibility across supplier pricing, market pricing, sales, invoicing, collections, and support operations',
    ],
    quote:
      'The platform now works as a connected operating layer between new digital services and legacy telco systems.',
    quoteBy: 'Enterprise telco integration case',
    tags: ['Legacy integration', 'OSS/BSS', 'Service desk'],
  },
  {
    id: 'tata-tele-nxtgen',
    client: 'Tata Tele NXTGEN Solutions',
    service: 'Telco-to-techco digital services platform',
    title: 'Telco-to-techco commercial transformation',
    industry: 'Telecommunications and Digital Services',
    status: 'PAN India transformation',
    logo: tataTeleLogo,
    summary:
      'A private-labelled digital services platform that helped TTNS build on its connectivity leadership, launch a multi-cloud and omnichannel services portfolio, and run direct enterprise, reseller, and referral partner journeys through one commercial engine.',
    metrics: [
      { value: '3x', label: 'revenue growth year 2' },
      { value: '6 wks', label: 'contract to live' },
      { value: '100Mn+', label: 'CDR records processed' },
      { value: '500K', label: 'annual transactions' },
    ],
    challenge: {
      title: 'Building a digital services growth engine on top of legacy telco strength',
      points: [
        'TTNS needed to launch cloud, email, security, CRM, managed services, UCaaS, CCaaS, and omnichannel solutions without disrupting existing connectivity relationships.',
        'The platform had to support direct enterprise customers, resellers, and referral partners simultaneously with self-serve journeys for standard deals and assisted journeys for complex enterprise transactions.',
        'Legacy billing and BSS systems could not support every commercial model together, including subscriptions, consumption, prepaid, postpaid, proration, bundles, CDR-rated voice, and partner settlement.',
      ],
    },
    solution: {
      title: 'Private-labelled marketplace, billing, and partner ecosystem layer',
      points: [
        'AppGallop deployed a private-labelled marketplace connecting T1 direct customers and T2 channel partners under one operating model with integrated onboarding, provisioning, billing, renewal, churn, and recommendation workflows.',
        'The billing and commerce engine handled subscription, consumption, prepaid, postpaid, pro-ration, ad-hoc, milestone-based, bundle billing, OEM reconciliation, and partner settlement inside one customer record.',
        'For omnichannel monetization, AppGallop connected catalog, orders, CDR mediation, UCaaS seats, CCaaS agent usage, WhatsApp conversation billing, RCS, Truecaller, DID, reseller margin rules, and invoice readiness in one audit trail.',
      ],
    },
    outcomes: [
      '3x revenue growth by year two while preserving existing telco relationships',
      'Contract-to-live timeline reduced to six weeks for the private-labelled commercial platform',
      'Ten-plus billing scenarios supported without a multi-year legacy BSS implementation',
      'Convergent UCaaS and CCaaS billing handled across voice, messaging, DID, agent usage, and reseller economics',
      'Partner onboarding, pricing, commission, rebate, settlement, ticketing, SLA, and escalation managed in one ecosystem layer',
      'New OEM, ISV, managed service, and sector-specific offerings can be packaged and extended through the marketplace and API layer',
    ],
    quote:
      'Connectivity was the foundation. Digital services became the growth engine. AppGallop made the transition possible without disrupting a single legacy relationship.',
    quoteBy: 'Tata Tele NXTGEN transformation case',
    tags: ['Telco-to-techco', 'UCaaS and CCaaS', 'Partner ecosystem'],
  },
  {
    id: 'redington',
    client: 'Redington',
    service: 'Enterprise lead-to-cash and distributor CPQ',
    title: 'Enterprise lead-to-cash transformation',
    industry: 'Technology Distribution',
    status: 'Live distributor commerce platform',
    logo: redingtonLogo,
    summary:
      'A centralized lead-to-cash platform that replaced email-heavy quotation workflows with governed CPQ, partner self-service, dynamic approvals, payment handling, SAP order creation, and invoice visibility.',
    metrics: [
      { value: '43K+', label: 'partners onboarded' },
      { value: '10K+', label: 'active quoting partners' },
      { value: '180+', label: 'quotes processed daily' },
      { value: '14', label: 'technology brands' },
    ],
    challenge: {
      title: 'Scaling distributor commerce beyond email-driven approvals',
      points: [
        'Quote requests moved through email, manual document checks, ERP credit validation, offline approvals, and repeated follow-ups across sales, business, finance, and operations teams.',
        'Each quote could generate 10 to 12 internal email exchanges and take up to two business days before approval completion.',
        'Redington needed standardized workflows, brand-specific governance, SAP integration, partner self-service, and full auditability across a large distributor ecosystem.',
      ],
    },
    solution: {
      title: 'Governed CPQ and SAP-connected lead-to-cash operating layer',
      points: [
        'AppGallop unified RFQ capture, quote creation, discount approvals, payment terms approvals, document validation, partner payment processing, SAP order creation, invoice sync, and collections tracking.',
        'Single Sign-On connected Redington Online with the CPQ platform so partners could create RFQs, upload documents, track approvals, monitor orders, and view invoices without separate authentication.',
        'Real-time SAP integration brought partner-specific pricing, discounts, credit limits, entitlements, and contract information into quote creation while enforcing material group-level governance.',
      ],
    },
    outcomes: [
      'Structured approval workflows replaced fragmented email coordination',
      'Material group-level rules enforce mandatory fields, documents, discount thresholds, and brand-specific governance',
      'SAP order, contract, purchase order, sales order, and invoice synchronization improved fulfillment visibility',
      'Redington Credit, deal-based credit, and Juspay payment flows support governed partner payment management',
      'Complete audit trails cover approvals, documents, quote versions, user activity, and status transitions',
      'Partners gained self-service visibility across quotation, order, invoice, and collection status',
    ],
    quote:
      'The platform became a strategic foundation for distributor commerce operations, enabling scale, governance, operational efficiency, and a stronger partner experience.',
    quoteBy: 'Enterprise distributor commerce case',
    tags: ['Lead-to-cash', 'SAP integration', 'Distributor CPQ'],
  },
] as const;

type CaseStudy = (typeof caseStudies)[number];

const getRelatedStudies = (currentId: string) =>
  caseStudies.filter((study) => study.id !== currentId).slice(0, 3);

const PageShell = ({ children }: { children: ReactNode }) => (
  <motion.main
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="relative isolate min-h-screen overflow-hidden bg-slate-50 pt-20 text-slate-950"
  >
    <div className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.055)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.045)_1px,transparent_1px)] bg-[size:48px_48px]" />
      <div className="absolute right-[-14%] top-[-10%] h-[460px] w-[460px] rounded-full bg-[#ff5d22]/10 blur-[110px]" />
      <div className="absolute bottom-[20%] left-[-18%] h-[380px] w-[380px] rounded-full bg-slate-900/8 blur-[120px]" />
    </div>
    {children}
  </motion.main>
);

const Hero = ({ compact = false }: { compact?: boolean }) => (
  <section className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-10">
    <div className="rounded-[1.75rem] border border-black/6 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] md:p-8">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[8px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
            Case Studies
          </div>
          <h1 className="mt-5 max-w-3xl text-3xl font-black leading-[0.98] tracking-tight text-slate-950 md:text-5xl">
            Enterprise outcomes across <span className="text-[#ff5d22]">commerce, billing, and legacy operations.</span>
          </h1>
        </div>
        <div>
          <p className="max-w-2xl text-sm font-bold leading-relaxed text-slate-500">
            {compact
              ? 'Explore the full case study, then move directly into another transformation story or return to the case study library.'
              : 'Choose a client to read the full transformation story. Each card shows the client, logo, and service area before opening the detailed case study.'}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {['Cloud distribution', 'Telecom integration', 'Revenue operations', 'Partner lifecycle'].map((tag) => (
              <span key={tag} className="rounded-full border border-black/8 bg-slate-50 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const LogoBlock = ({ study }: { study: CaseStudy }) => (
  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-black/8 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
    <img src={study.logo} alt={study.client} className="max-h-10 max-w-[128px] object-contain" />
  </div>
);

const CaseStudyIndexPage = () => (
  <PageShell>
    <Hero />

    <section className="mx-auto max-w-7xl px-6 pb-16 md:px-10 md:pb-24">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {caseStudies.map((study, index) => (
          <motion.div
            key={study.id}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
          >
            <Link
              to={`/case-studies/${study.id}`}
              className="group flex h-full flex-col justify-between overflow-hidden rounded-[1.5rem] border border-black/8 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-[#ff5d22]/35 hover:shadow-[0_24px_70px_rgba(15,23,42,0.1)]"
            >
              <div>
                <div className="flex items-start justify-between gap-5">
                  <LogoBlock study={study} />
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-slate-500">
                    {study.status}
                  </span>
                </div>

                <div className="mt-8">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                    {study.industry}
                  </p>
                  <h2 className="mt-3 text-2xl font-black leading-tight tracking-tight text-slate-950 md:text-3xl">
                    {study.client}
                  </h2>
                  <p className="mt-3 text-sm font-black leading-relaxed text-slate-700">
                    {study.service}
                  </p>
                  <p className="mt-4 line-clamp-3 text-sm font-bold leading-relaxed text-slate-500">
                    {study.summary}
                  </p>
                </div>
              </div>

              <div className="mt-7 flex items-center justify-between border-t border-black/6 pt-5">
                <div className="flex flex-wrap gap-2">
                  {study.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-[#d9481e]">
                      {tag}
                    </span>
                  ))}
                </div>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-950 text-white transition-all group-hover:bg-[#ff5d22]">
                  <ArrowRight size={17} />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="pt-8">
        <ContactSection id="contact" />
      </div>
    </section>
  </PageShell>
);

const CaseStudyDetailPage = ({ study }: { study: CaseStudy }) => {
  const relatedStudies = getRelatedStudies(study.id);

  return (
    <PageShell>
      <Hero compact />

      <section className="mx-auto max-w-7xl px-6 pb-16 md:px-10 md:pb-24">
        <Link
          to="/case-studies"
          className="mb-5 inline-flex min-h-10 items-center gap-2 rounded-full border border-black/8 bg-white px-4 text-[10px] font-black uppercase tracking-[0.16em] text-slate-600 transition-all hover:border-[#ff5d22]/30 hover:text-[#ff5d22]"
        >
          <ArrowLeft size={14} />
          Back to case studies
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-[1.75rem] border border-slate-950/12 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.07)]"
        >
          <div className="bg-slate-950 p-6 text-white md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <LogoBlock study={study} />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff8a5c]">
                      {study.industry}
                    </p>
                    <span className="rounded-full border border-white/30 px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.14em] text-white/75">
                      {study.status}
                    </span>
                  </div>
                  <h2 className="mt-3 max-w-3xl text-2xl font-black leading-tight tracking-tight text-white md:text-4xl">
                    {study.client}: {study.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm font-black leading-relaxed text-white/72">
                    {study.service}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-white/12 pt-5 sm:grid-cols-4 lg:max-w-[460px] lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                {study.metrics.map((metric) => (
                  <div key={`${study.client}-${metric.label}`}>
                    <p className="text-xl font-black tracking-tight text-white">{metric.value}</p>
                    <p className="mt-1 text-[8px] font-black uppercase tracking-[0.14em] text-white/40">
                      {metric.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-8 max-w-4xl text-sm font-bold leading-relaxed text-white/64">
              {study.summary}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-0 lg:grid-cols-3">
            <div className="border-b border-black/6 p-6 md:p-7 lg:border-b-0 lg:border-r">
              <div className="flex items-center gap-2 text-[#ff5d22]">
                <FileCheck size={17} />
                <p className="text-[9px] font-black uppercase tracking-[0.2em]">Challenge</p>
              </div>
              <h3 className="mt-3 text-lg font-black leading-tight tracking-tight text-slate-950">{study.challenge.title}</h3>
              <div className="mt-4 space-y-3">
                {study.challenge.points.map((point) => (
                  <p key={point} className="text-sm font-bold leading-relaxed text-slate-500">
                    {point}
                  </p>
                ))}
              </div>
            </div>

            <div className="border-b border-black/6 p-6 md:p-7 lg:border-b-0 lg:border-r">
              <div className="flex items-center gap-2 text-[#ff5d22]">
                <Network size={17} />
                <p className="text-[9px] font-black uppercase tracking-[0.2em]">Solution</p>
              </div>
              <h3 className="mt-3 text-lg font-black leading-tight tracking-tight text-slate-950">{study.solution.title}</h3>
              <div className="mt-4 space-y-3">
                {study.solution.points.map((point) => (
                  <div key={point} className="flex items-start gap-2.5">
                    <Check size={14} className="mt-1 shrink-0 text-emerald-500" />
                    <p className="text-sm font-bold leading-relaxed text-slate-500">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 md:p-7">
              <div className="flex items-center gap-2 text-[#ff5d22]">
                <ShieldCheck size={17} />
                <p className="text-[9px] font-black uppercase tracking-[0.2em]">Outcomes</p>
              </div>
              <div className="mt-4 space-y-2.5">
                {study.outcomes.map((outcome) => (
                  <div key={outcome} className="flex items-start gap-2.5">
                    <Check size={14} className="mt-1 shrink-0 text-[#ff5d22]" />
                    <p className="text-sm font-bold leading-relaxed text-slate-600">{outcome}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-black/6 px-6 py-5 md:px-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {study.tags.map((tag) => (
                  <span key={tag} className="rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-[#d9481e]">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="max-w-2xl lg:text-right">
                <p className="text-sm font-black leading-relaxed text-slate-800">"{study.quote}"</p>
                <p className="mt-1 text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">{study.quoteBy}</p>
              </div>
            </div>
          </div>
        </motion.article>

        <section className="mt-8">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">Explore More</p>
              <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Other case studies</h3>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex min-h-10 items-center gap-2 rounded-full bg-slate-950 px-4 text-[10px] font-black uppercase tracking-[0.16em] text-white transition-all hover:bg-[#ff5d22]"
            >
              View all
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {relatedStudies.map((related) => (
              <Link
                key={related.id}
                to={`/case-studies/${related.id}`}
                className="group rounded-[1.25rem] border border-black/8 bg-white p-5 shadow-[0_14px_45px_rgba(15,23,42,0.05)] transition-all hover:-translate-y-1 hover:border-[#ff5d22]/30"
              >
                <div className="flex items-center gap-3">
                  <LogoBlock study={related} />
                  <div>
                    <p className="text-base font-black leading-tight text-slate-950">{related.client}</p>
                    <p className="mt-1 text-xs font-bold leading-snug text-slate-500">{related.service}</p>
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-[#ff5d22]">
                  Read case study
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="pt-8">
          <ContactSection id="contact" />
        </div>
      </section>
    </PageShell>
  );
};

const CaseStudiesPage = () => {
  const { caseStudyId } = useParams();

  if (!caseStudyId) {
    return <CaseStudyIndexPage />;
  }

  const study = caseStudies.find((item) => item.id === caseStudyId);

  if (!study) {
    return <Navigate to="/case-studies" replace />;
  }

  return <CaseStudyDetailPage study={study} />;
};

export default CaseStudiesPage;
